import { MetadataRegion, Region, RegionContainerSize } from "./region.js";
import { Field } from "./field.js";
import { Helper } from "../util/helper.js";
import { ErrorBoundary } from "../../statistics/components/error_boundary.js";
import { MetaDataHelper } from "../util/metaDataHelper.js";
import { FullHeightContainer } from "./FullHeighContainer.js";
import { GridLayoutBreakPointConfig, BreakPoint, GridLayoutItem, SimpleGridLayout } from "./gridLayout.js";
import ExtendedViewButton from "./extendedViewButton.js";
import { CommonAppBaseProps } from "./commonApp.js";
import { BaseField } from "./baseComponentProps.js";
import React from "react";
import { Config } from "../config/config.js";
export class FormGenerator extends React.Component<FormGeneratorProps, {
    isExtended: boolean;
    extendedRegions: Map<string, boolean>;
}> {
    private fieldRefs = {};

    constructor(props) {
        super(props);
        this.state = {
            isExtended: false,
            extendedRegions: new Map<string, boolean>()
        }
    }

    private _containerToBreakPoint = (container: RegionContainerSize): BreakPoint => {
        switch (container) {
            case "extrasmall": return BreakPoint.ExtraSmall;
            case "small": return BreakPoint.Small;
            case "medium": return BreakPoint.Medium;
            case "large": return BreakPoint.Large;
            case "extralarge": return BreakPoint.ExtraLarge;
            default: return null;
        }
    }

    componentDidMount() {
        this.forceUpdate();
    }

    setFieldRef = (field: any, element) => {
        this.fieldRefs[field.guid] = { element, field };
        if (!element) {
            delete this.fieldRefs[field.guid];
        }
    }

    createField = (fieldIndex: number, fieldMetadata: BaseField, dataSourceRef, region: MetadataRegion) => {
        const fieldName = this.props.debug && fieldMetadata["iskey"] ? <FieldMetadataViewer metadata={fieldMetadata} backgroundColor="lightgreen" /> : null;

        const field = <ErrorBoundary key={fieldMetadata.guid || fieldIndex}>
            {fieldName}
            <Field
                ref={this.setFieldRef.bind(this, fieldMetadata)}
                field={fieldMetadata}
                entity={this.props.entity}
                dataSource={dataSourceRef}
                context={this.props.context}
                handleEval={this.props.handleEval}
                handleIsSearchFieldFullObjectModel={this.props.handleIsSearchFieldFullObjectModel}
                onChange={this.props.onChange}
                onDataLoaded={this.props.onDataLoaded}
                commandManager={this.props.commandManager} />
        </ErrorBoundary>

        if (region.enableGridLayout) {
            return <GridLayoutItem layoutConfig={[{ breakPoint: BreakPoint.Small, size: "12/12" }]}>{field}</GridLayoutItem>
        }
        return field;
    }

    private isExtendedViewField = (field: BaseField) => {
        return ((field.extendedView !== undefined && field.extendedView === true) || (typeof (field.extendedView) === "string" && this.props.handleEval(field.extendedView as string)));
    }

    private isExtendedViewRegion = (region: MetadataRegion) => {
        return ((region.extendedView !== undefined && region.extendedView === true) || (typeof (region.extendedView) === "string" && this.props.handleEval(region.extendedView as string)));
    }

    private extendedViewRegionClick = (isExtended: boolean, region: MetadataRegion) => {
        let extendedRegions = this.state.extendedRegions;
        extendedRegions.set(region.guid, isExtended);
        this.setState({ extendedRegions: extendedRegions })
    }

    private extendedViewClick = (isExtended: boolean) => {
        let extendedRegions: Map<string, boolean> = new Map<string, boolean>();
        this.props.regions.forEach(x => extendedRegions.set(x.guid, isExtended));
        this.setState({ isExtended: isExtended, extendedRegions: extendedRegions });
    }

    render() {
        const regions = [];
        if (Array.isArray(this.props.regions)) {
            const layoutRegions = MetaDataHelper.getLayoutRegions(this.props.regions).map(r => r.guid);
            this.props.regions.forEach((region, index) => {
                const forceBreak = index > 0 ? !!this.props.regions[index - 1].breakStart : false;
                let renderRegion = false;
                const fields = [];
                const func: Function = region.visible as Function;
                const appData = { entity: this.props.entity };

                if (layoutRegions.indexOf(region.guid) !== -1 && (!Helper.hasValue(region.visible) || (typeof region.visible === 'function' ? func(appData) : this.props.handleEval(region.visible as any)))) {
                    renderRegion = true;
                } else {
                    const countDisplayedFields = !Array.isArray(region.fields) ? 0 : region.fields.filter(f => !f.noSearchResult).length;
                    if ((countDisplayedFields > 0) && (!region.noSearchResult)) {
                        if (!Helper.hasValue(region.visible) || (typeof region.visible === 'function' ? func(appData) : this.props.handleEval(region.visible as any))) {
                            if (Array.isArray(region.fields)) {
                                region.fields.forEach((field, fieldIndex) => {
                                    let isExtenedView = this.isExtendedViewField(field);
                                    if (!Field.isInvisible(field.visible, field.visibleCondition, this.props.handleEval, field.noSearchResult, appData) && (!isExtenedView || (isExtenedView && this.state.extendedRegions.get(region.guid)))) {
                                        const dataSourceRef = Helper.hasValue(field.dataSource) ? Helper.getValueByPathString(this.props.dataSources, field.dataSource) : null;
                                        fields.push(this.createField(fieldIndex, field, dataSourceRef, region));
                                    }
                                });
                            }
                            renderRegion = true;
                        }
                    }
                }
                const conf: GridLayoutBreakPointConfig[] = [];
                if (!region.size) {
                } else {
                    if (Array.isArray(region.size)) {
                        region.size.forEach(rs => conf.push({ breakPoint: this._containerToBreakPoint(rs.container) ?? BreakPoint.ExtraSmall, size: rs.width, rowSize: rs.rowSize }));
                    } else {
                        conf.push({ breakPoint: this._containerToBreakPoint(region.size.container), size: region.size.width, rowSize: region.size.rowSize });
                    }
                }
                const regionName = this.props.debug && region["iskey"] ? <MetadataNameViewer metadataName={region["iskey"]} backgroundColor="lightgray" /> : null;
                let isExtenedView = this.isExtendedViewRegion(region);
                if (renderRegion && !region.breakStart && (!isExtenedView || (isExtenedView && this.state.isExtended))) {
                    regions.push(
                        <GridLayoutItem layoutConfig={conf} gridColumnStart={forceBreak ? 1 : null} key={region.guid || index} renderCollapsed={region.renderCollapsed}>
                            {regionName}
                            <Region key={region.guid || index} title={region.title}
                                id={region.id}
                                width={region.width}
                                minWidth={region.minWidth}
                                maxWidth={region.maxWidth}
                                renderHeader={region.renderHeader}
                                size={region.size}
                                enableGridLayout={region.enableGridLayout}>
                                {!region.enableGridLayout ? fields : <SimpleGridLayout>{fields}</SimpleGridLayout>}
                                <ExtendedViewButton isExtended={this.state.extendedRegions.get(region.guid)} onClick={(isExtended) => this.extendedViewRegionClick(isExtended, region)}
                                    visible={region.fields?.filter(x => this.isExtendedViewField(x)).length > 0} />
                            </Region>
                        </GridLayoutItem>
                    );
                }
            });
        }
        return (
            <FullHeightContainer>
                <SimpleGridLayout>
                    {regions}
                </SimpleGridLayout>
                <ExtendedViewButton isExtended={this.state.isExtended} onClick={(isExtended) => this.extendedViewClick(isExtended)}
                    visible={this.props.regions.filter(x => this.isExtendedViewRegion(x) || x.fields?.filter(x => this.isExtendedViewField(x)).length > 0).length > 0}
                    wrapperClass={"sheet__actions__extended-view"} />
            </FullHeightContainer>
        );
    }
};

export class MetadataNameViewer extends React.Component<{ metadataName: string, backgroundColor: string }, any> {

    render() {
        return <div style={{ fontSize: "10px", backgroundColor: this.props.backgroundColor ?? "lightgreen", fontWeight: "bold", margin: "2px" }}> {this.props.metadataName}</div>
    }
}

export class FieldMetadataViewer extends React.Component<{ metadata: any, backgroundColor: string }, any> {

    render() {
        if (!this.props.metadata) {
            return null;
        }
        if (this.props.metadata.type == "statistic") {
            return <div style={{ fontSize: "12px", backgroundColor: this.props.backgroundColor ?? "lightgreen", fontWeight: "bold", margin: "2px" }}> {this.props.metadata["iskey"]}
                <div><a href={`${Config.baseUrl}Statistic/uiBuilderDetail?id=${this.props.metadata["statisticId"]}`} target="_blank">{this.props.metadata["statisticId"]}</a></div>
            </div>
        }
        return <div style={{ fontSize: "12px", backgroundColor: this.props.backgroundColor ?? "lightgreen", fontWeight: "bold", margin: "2px" }}> {this.props.metadata["iskey"]}({this.props.metadata["model"]})</div>
    }
}

interface FormGeneratorProps extends CommonAppBaseProps {
    dataSources: any;
    debugMode?: boolean;
    regions: Array<MetadataRegion>;
    debug?: boolean;
}
